/**
 * Type definition — navigates to the `type` declaration that defines
 * the kind of the symbol under the cursor.
 *
 *   microservice Orders { ... }
 *                ^ cursor here → goes to `type microservice ...`
 *
 * Falls back to the regular definition target when no kind is set.
 */

import type { Location } from "vscode-languageserver";
import type { CursorContext } from "../workspace/cursor-context.js";
import type { SymbolTable } from "../workspace/symbol-table.js";
import type { Workspace } from "../workspace/workspace.js";
import { resolveSymbolAtCursor } from "../workspace/resolve.js";
import { toLspRange } from "../lsp-types.js";

export function provideTypeDefinition(
  workspace: Workspace,
  symbols: SymbolTable,
  cursor: CursorContext,
  uri: string,
  line: number,
  character: number
): Location | null {
  const sym = resolveSymbolAtCursor(workspace, symbols, cursor, uri, line, character);
  if (!sym || !sym.typeName) return null;

  // Look up the type declaration whose name matches the typeName — from the
  // SYMBOL's own file (v0.13): a twin type name resolves in the space the
  // instance landed in, not the referrer's cursor file.
  const typeSym = symbols.resolve(sym.typeName, sym.definingUri ?? uri);
  if (!typeSym || typeSym.kind !== "type") return null;
  const span = typeSym.nameSpan ?? typeSym.declSpan;
  if (!typeSym.definingUri || !span) return null;
  return { uri: typeSym.definingUri, range: toLspRange(span) };
}
